import type { SortOrder } from '../types';

/**
 * 🎯 UI 컴포넌트 이벤트 계약 — 이벤트 이름 ↔ detail 모양의 단일 출처(single source of truth)
 *
 * 배경:
 *   Sola 의 emit / events 위임은 이벤트 이름과 detail 모양을 타입으로 강제하지 않는다.
 *   보내는 쪽 `this.emit('x', {...})` 과 받는 쪽 `(e as CustomEvent).detail as {...}` 이
 *   서로 분리돼 있어서, 한쪽 모양이 바뀌어도 다른 쪽이 무검증 캐스팅으로 조용히 통과한다.
 *
 *   이 맵이 그 둘의 모양을 한 곳에서 정의한다. 이벤트의 detail 모양이 어긋나면
 *   `detailOf()` 를 쓰는 모든 수신부에서 컴파일 타임에 잡힌다.
 *
 * 새 컴포넌트 이벤트를 추가하려면:
 *   1. 여기 한 줄 추가:  'my:event': { ... };
 *   2. 보내는 쪽:        this.emit('my:event', <UiEventMap['my:event'] 모양>)
 *   3. 받는 쪽:          const d = detailOf(e, 'my:event');   // 타입 자동
 *
 * 참고: 'table:select'(Sidebar) 등은 현재 사용처가 죽은 코드(AdminApp/Sidebar)라
 *       의도적으로 제외했다. 해당 컴포넌트를 되살리면 그때 추가한다.
 */
export interface UiEventMap {
  /** Pager → 페이지 이동 (offset/limit 변경) */
  'page:change': { offset: number; limit: number };

  /** DataGrid → 컬럼 헤더 클릭으로 정렬 토글 */
  'sort:change': { col: string; order: SortOrder };

  /** DataGrid → 행 클릭으로 상세 열기 */
  'row:open': { row: Record<string, unknown> };
}

/**
 * 🎯 타입드 detail 리더 — 받는 쪽의 `(e as CustomEvent).detail as {...}` 무검증 캐스팅을 대체.
 *
 * 사용:
 *   handlePage(e: Event): void {
 *     const { offset, limit } = detailOf(e, 'page:change');  // 타입 자동 추론
 *   }
 *
 * - 두 번째 인자(eventName)는 런타임에 쓰이지 않는다. 오직 반환 타입을 고르기 위한 키다.
 *   (detail 의 실제 값은 CustomEvent 에 이미 들어있고, 어느 이벤트인지는 호출자가 안다.)
 * - 컴파일 타임 계약이다. detail 이 우리 emit 에서 온다는 신뢰 위에 동작하며,
 *   외부/비신뢰 소스의 이벤트에는 별도 런타임 검증이 필요하다.
 */
export function detailOf<K extends keyof UiEventMap>(
  e: Event,
  _eventName: K
): UiEventMap[K] {
  return (e as CustomEvent<UiEventMap[K]>).detail;
}
